import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { StatCard } from '../components/StatCard';

const short = (addr) => addr ? `${addr.slice(0,5)}…${addr.slice(-4)}` : '—';
const fmtTime = (d) => new Date(d).toLocaleString();

const EVENT_COLORS = {
  swap:           '#00ff88',
  token_transfer: '#ffd166',
  sol_transfer:   '#4d9eff',
  unknown:        '#7a94b0',
};

export const ActivityPage = ({ toast }) => {
  const [activities, setActivities] = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('');
  const [page,       setPage]       = useState(1);
  const [pagination, setPagination] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { limit: 25, page };
      if (filter) q.eventType = filter;
      const [ar, sr] = await Promise.all([api.getActivity(q), api.getStats()]);
      setActivities(ar.data || []);
      setPagination(ar.pagination);
      setStats(sr.data);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filter, page, toast]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  return (
    <div style={S.page}>
      {/* Stats row */}
      {stats && (
        <div style={S.statsRow}>
          <StatCard icon="📊" label="Total Transactions" value={stats.totalTx} />
          <StatCard icon="🔄" label="Swaps" value={stats.swaps} accent="#00ff88" />
          <StatCard icon="💸" label="Transfers" value={stats.transfers} accent="#ffd166" />
          <StatCard icon="🕐" label="Last 24h" value={stats.last24h} accent="#4d9eff" />
        </div>
      )}

      {/* Feed controls */}
      <div style={S.card}>
        <div style={S.controls}>
          <h2 style={S.cardTitle}>Live Activity Feed</h2>
          <div style={S.controlRight}>
            <label style={{ display:'inline' }}>
              <input type="checkbox" checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)} style={{ width:'auto', marginRight:6 }} />
              Auto-refresh (10s)
            </label>
            <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }} style={S.filterSel}>
              <option value="">All types</option>
              <option value="swap">Swaps only</option>
              <option value="token_transfer">Token transfers</option>
              <option value="sol_transfer">SOL transfers</option>
            </select>
            <button style={S.btnRefresh} onClick={load}>↻ Refresh</button>
          </div>
        </div>

        {loading && activities.length === 0 ? (
          <p style={S.dim}>Loading activity…</p>
        ) : activities.length === 0 ? (
          <p style={S.dim}>No activity recorded yet. Add a wallet to start tracking.</p>
        ) : (
          <>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Type','Wallet','Token Bought','Amount','Signature','Time'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activities.map(a => {
                    const color = EVENT_COLORS[a.eventType] || '#7a94b0';
                    return (
                      <tr key={a._id} style={S.tr}>
                        <td style={S.td}>
                          <span style={{ ...S.tag, color, borderColor: color+'40', background: color+'10' }}>
                            {a.eventType}
                          </span>
                        </td>
                        <td style={S.td}><code style={S.mono}>{short(a.walletAddress)}</code></td>
                        <td style={S.td}>
                          {a.tokenBought
                            ? <code style={S.mono}>{short(a.tokenBought)}</code>
                            : <span style={S.na}>—</span>}
                        </td>
                        <td style={S.td}>
                          {a.amountBought ? a.amountBought.toFixed(4) : <span style={S.na}>—</span>}
                        </td>
                        <td style={S.td}>
                          <a href={`https://solscan.io/tx/${a.signature}`} target="_blank" rel="noreferrer" style={S.txLink}>
                            {short(a.signature)} ↗
                          </a>
                        </td>
                        <td style={{ ...S.td, color:'#7a94b0', fontSize:11 }}>{fmtTime(a.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div style={S.pagination}>
                <button style={S.btnSm} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={S.pageInfo}>Page {page} of {pagination.pages} ({pagination.total} total)</span>
                <button style={S.btnSm} disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Top tokens table */}
      {stats?.topTokens?.length > 0 && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Top Tokens Bought (7d)</h2>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['#','Token Mint','Buy Count','Total Volume'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.topTokens.map((t, i) => (
                  <tr key={t._id} style={S.tr}>
                    <td style={{ ...S.td, color:'#00ff88', fontFamily:"'Space Mono',monospace" }}>#{i + 1}</td>
                    <td style={S.td}>
                      <a href={`https://solscan.io/token/${t._id}`} target="_blank" rel="noreferrer" style={S.txLink}>
                        <code style={S.mono}>{t._id}</code> ↗
                      </a>
                    </td>
                    <td style={S.td}>{t.count}</td>
                    <td style={S.td}>{t.totalVolume?.toFixed(2) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const S = {
  page:       { display:'flex', flexDirection:'column', gap:24 },
  statsRow:   { display:'flex', gap:16, flexWrap:'wrap' },
  card:       { background:'#0d1117', border:'1px solid #1e2d3d', borderRadius:12, padding:'24px 28px' },
  controls:   { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:12, flexWrap:'wrap' },
  controlRight:{ display:'flex', alignItems:'center', gap:12 },
  cardTitle:  { fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18 },
  filterSel:  { width:'auto', padding:'7px 12px' },
  btnRefresh: { background:'#131920', color:'#e0eaf5', border:'1px solid #1e2d3d', padding:'7px 16px', borderRadius:6, fontSize:13 },
  btnSm:      { background:'#131920', color:'#e0eaf5', border:'1px solid #1e2d3d', padding:'6px 14px', fontSize:12, borderRadius:6 },
  dim:        { color:'#7a94b0', fontStyle:'italic', padding:'8px 0' },
  tableWrap:  { overflowX:'auto' },
  table:      { width:'100%', borderCollapse:'collapse' },
  th:         { textAlign:'left', padding:'10px 14px', fontSize:10, fontWeight:700,
                letterSpacing:'0.08em', textTransform:'uppercase', color:'#7a94b0',
                borderBottom:'1px solid #1e2d3d' },
  tr:         { borderBottom:'1px solid #131920' },
  td:         { padding:'11px 14px', fontSize:13, verticalAlign:'middle' },
  tag:        { fontSize:10, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase',
                padding:'3px 8px', borderRadius:4, border:'1px solid' },
  mono:       { fontFamily:"'Space Mono',monospace", fontSize:11, color:'#e0eaf5' },
  na:         { color:'#7a94b0' },
  txLink:     { color:'#7a94b0', fontFamily:"'Space Mono',monospace", fontSize:11 },
  pagination: { display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginTop:20 },
  pageInfo:   { fontSize:12, color:'#7a94b0', fontFamily:"'Space Mono',monospace" },
};
